import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Save, X, Eye, EyeOff, PlayCircle, BarChart3, Upload, Link, CloudUpload } from 'lucide-react';
import { toast } from 'sonner';
import '../styles/VideoManager.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideoManager = () => {
  const [videos, setVideos] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingVideo, setEditingVideo] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [activeView, setActiveView] = useState('list');
  const [uploadMode, setUploadMode] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    title_en: '',
    title_es: '',
    url: '',
    description: '',
    description_en: '',
    description_es: '',
    position: 1,
    is_active: true,
    partner_url: '',
    partner_button_de: '',
    partner_button_en: '',
    partner_button_es: ''
  });

  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    loadVideos();
    loadAnalytics();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const loadVideos = async () => {
    try {
      const response = await axios.get(`${API}/admin/videos`, getAuthHeader());
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error loading videos:', error);
      toast.error('Fehler beim Laden der Videos');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/videos/analytics`, getAuthHeader());
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const convertToEmbedUrl = (url) => {
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    return url;
  };

  const isCloudinaryUrl = (url) => {
    return url && url.includes('cloudinary.com');
  };

  const isYoutubeUrl = (url) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be'));
  };

  const handleFileUpload = async () => {
    if (!uploadFile || !formData.title) {
      toast.error('Bitte Titel eingeben und Video-Datei auswählen');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formDataUpload = new FormData();
    formDataUpload.append('file', uploadFile);
    formDataUpload.append('title', formData.title);
    formDataUpload.append('description', formData.description || '');
    formDataUpload.append('position', formData.position);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API}/admin/videos/upload`,
        formDataUpload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      toast.success('Video erfolgreich hochgeladen!');
      setShowCreateForm(false);
      resetForm();
      loadVideos();
      loadAnalytics();
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen des Videos');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCreate = async () => {
    if (uploadMode === 'upload') {
      await handleFileUpload();
      return;
    }

    if (!formData.title || !formData.url) {
      toast.error('Bitte Titel und URL eingeben');
      return;
    }

    try {
      const embedUrl = convertToEmbedUrl(formData.url);
      const videoData = { 
        ...formData, 
        url: embedUrl,
        video_type: isYoutubeUrl(embedUrl) ? 'youtube' : 'external'
      };
      await axios.post(`${API}/admin/videos`, videoData, getAuthHeader());
      toast.success('Video erfolgreich erstellt');
      setShowCreateForm(false);
      resetForm();
      loadVideos();
      loadAnalytics();
    } catch (error) {
      console.error('Error creating video:', error);
      toast.error('Fehler beim Erstellen des Videos');
    }
  };

  const handleUpdate = async (videoId) => {
    if (!editingVideo.title || !editingVideo.url) {
      toast.error('Bitte Titel und URL eingeben');
      return;
    }

    try {
      const embedUrl = convertToEmbedUrl(editingVideo.url);
      await axios.put(`${API}/admin/videos/${videoId}`, { ...editingVideo, url: embedUrl }, getAuthHeader());
      toast.success('Video erfolgreich aktualisiert');
      setEditingVideo(null);
      loadVideos();
      loadAnalytics();
    } catch (error) {
      console.error('Error updating video:', error);
      toast.error('Fehler beim Aktualisieren des Videos');
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Möchten Sie dieses Video wirklich löschen?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/videos/${videoId}`, getAuthHeader());
      toast.success('Video erfolgreich gelöscht');
      loadVideos();
      loadAnalytics();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Fehler beim Löschen des Videos');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      title_en: '',
      title_es: '',
      url: '',
      description: '',
      description_en: '',
      description_es: '',
      position: 1,
      is_active: true,
      partner_url: '',
      partner_button_de: '',
      partner_button_en: '',
      partner_button_es: ''
    });
    setUploadFile(null);
    setUploadMode('url');
  };

  const startEditing = (video) => {
    setEditingVideo({ ...video });
  };

  const cancelEditing = () => {
    setEditingVideo(null);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('video/')) {
        setUploadFile(file);
        setUploadMode('upload');
        if (!formData.title) {
          setFormData(prev => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, '') }));
        }
      } else {
        toast.error('Bitte nur Video-Dateien (MP4, MOV, WebM) hochladen');
      }
    }
  }, [formData.title]);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadFile(file);
      if (!formData.title) {
        setFormData(prev => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, '') }));
      }
    }
  };

  const renderVideoPreview = (video) => {
    if (isCloudinaryUrl(video.url)) {
      return (
        <video
          src={video.url}
          className="video-preview-player"
          controls
          muted
          preload="metadata"
        />
      );
    } else if (isYoutubeUrl(video.url)) {
      return (
        <iframe
          src={video.url}
          title={video.title}
          frameBorder="0"
          className="video-preview-iframe"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    } else {
      return (
        <video
          src={video.url}
          className="video-preview-player"
          controls
          muted
          preload="metadata"
        />
      );
    }
  };

  if (loading) {
    return <div className="video-manager-loading">Lade Videos...</div>;
  }

  return (
    <div className="video-manager">
      <div className="video-manager-header">
        <h2>Video Verwaltung</h2>
        <div className="header-actions">
          <button
            className={`view-toggle ${activeView === 'list' ? 'active' : ''}`}
            onClick={() => setActiveView('list')}
          >
            <PlayCircle size={18} />
            Videos
          </button>
          <button
            className={`view-toggle ${activeView === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveView('analytics')}
          >
            <BarChart3 size={18} />
            Analytics
          </button>
          {!showCreateForm && activeView === 'list' && (
            <button
              className="btn-create"
              onClick={() => setShowCreateForm(true)}
            >
              <Plus size={18} />
              Neues Video
            </button>
          )}
        </div>
      </div>

      {activeView === 'analytics' && analytics && (
        <div className="analytics-view">
          <div className="analytics-summary">
            <div className="analytics-card">
              <div className="analytics-label">Gesamt Videos</div>
              <div className="analytics-value">{analytics.total_videos}</div>
            </div>
            <div className="analytics-card">
              <div className="analytics-label">Gesamt Klicks</div>
              <div className="analytics-value">{analytics.total_clicks}</div>
            </div>
            <div className="analytics-card">
              <div className="analytics-label">Ø Klicks pro Video</div>
              <div className="analytics-value">
                {analytics.total_videos > 0 
                  ? (analytics.total_clicks / analytics.total_videos).toFixed(1)
                  : '0'}
              </div>
            </div>
          </div>

          <div className="analytics-table">
            <h3>Video Performance</h3>
            <table>
              <thead>
                <tr>
                  <th>Position</th>
                  <th>Titel</th>
                  <th>Status</th>
                  <th>Gesamt Klicks</th>
                  <th>Letzte 7 Tage</th>
                </tr>
              </thead>
              <tbody>
                {analytics.analytics.map((video) => (
                  <tr key={video.video_id}>
                    <td>{video.position}</td>
                    <td>{video.title}</td>
                    <td>
                      <span className={`status-badge ${video.is_active ? 'active' : 'inactive'}`}>
                        {video.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="number">{video.total_clicks}</td>
                    <td className="number">{video.clicks_last_7_days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeView === 'list' && (
        <div className="videos-list">
          {showCreateForm && (
            <div className="video-form create-form">
              <h3>Neues Video erstellen</h3>
              
              <div className="upload-mode-toggle">
                <button
                  className={`mode-btn ${uploadMode === 'url' ? 'active' : ''}`}
                  onClick={() => setUploadMode('url')}
                >
                  <Link size={18} />
                  URL eingeben
                </button>
                <button
                  className={`mode-btn ${uploadMode === 'upload' ? 'active' : ''}`}
                  onClick={() => setUploadMode('upload')}
                >
                  <CloudUpload size={18} />
                  Video hochladen
                </button>
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <label>Titel * (Deutsch)</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="z.B. Wedding-O-Mat Teaser"
                  />
                </div>
                <div className="form-field">
                  <label>Title (English)</label>
                  <input
                    type="text"
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                    placeholder="e.g. Wedding-O-Mat Teaser"
                  />
                </div>
                <div className="form-field">
                  <label>Titulo (Espanol)</label>
                  <input
                    type="text"
                    value={formData.title_es}
                    onChange={(e) => setFormData({ ...formData, title_es: e.target.value })}
                    placeholder="ej. Wedding-O-Mat Teaser"
                  />
                </div>
                <div className="form-field">
                  <label>Position</label>
                  <select
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) })}
                  >
                    <option value={1}>1 (Startseite Links)</option>
                    <option value={2}>2 (Startseite Mitte)</option>
                    <option value={3}>3 (Startseite Rechts)</option>
                    <option value={4}>4 (Quiz nach Frage 8)</option>
                  </select>
                </div>

                {uploadMode === 'url' ? (
                  <div className="form-field full-width">
                    <label>Video URL * (YouTube oder direkte URL)</label>
                    <input
                      type="text"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://www.youtube.com/watch?v=... oder https://example.com/video.mp4"
                    />
                    <small className="form-hint">
                      Unterstuetzt: YouTube Links, YouTube Embed URLs, direkte Video-URLs
                    </small>
                  </div>
                ) : (
                  <div className="form-field full-width">
                    <label>Video-Datei hochladen (max. 500MB)</label>
                    <div
                      className={`video-upload-zone ${dragActive ? 'drag-active' : ''} ${uploadFile ? 'has-file' : ''}`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      {uploadFile ? (
                        <div className="upload-file-info">
                          <PlayCircle size={32} />
                          <span className="file-name">{uploadFile.name}</span>
                          <span className="file-size">
                            {(uploadFile.size / (1024 * 1024)).toFixed(2)} MB
                          </span>
                          <button 
                            className="btn-remove-file"
                            onClick={() => setUploadFile(null)}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <Upload size={32} />
                          <p>Video hier ablegen oder klicken zum Auswaehlen</p>
                          <small>MP4, MOV, WebM, AVI - Max. 500MB</small>
                        </>
                      )}
                      <input
                        type="file"
                        accept="video/*"
                        onChange={handleFileSelect}
                        className="file-input-hidden"
                      />
                    </div>
                    {uploading && (
                      <div className="upload-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <span className="progress-text">{uploadProgress}% hochgeladen</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="form-field full-width">
                  <label>Beschreibung (Deutsch)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Kurze Beschreibung des Videos"
                    rows={2}
                  />
                </div>
                <div className="form-field full-width">
                  <label>Description (English)</label>
                  <textarea
                    value={formData.description_en}
                    onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                    placeholder="Short video description"
                    rows={2}
                  />
                </div>
                <div className="form-field full-width">
                  <label>Descripcion (Espanol)</label>
                  <textarea
                    value={formData.description_es}
                    onChange={(e) => setFormData({ ...formData, description_es: e.target.value })}
                    placeholder="Breve descripcion del video"
                    rows={2}
                  />
                </div>
                {formData.position === 4 && (
                  <>
                    <div className="form-field full-width" style={{borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px'}}>
                      <label style={{fontWeight: 600, color: 'var(--color-violett)'}}>Werbepartner-Link</label>
                      <input
                        type="url"
                        value={formData.partner_url}
                        onChange={(e) => setFormData({ ...formData, partner_url: e.target.value })}
                        placeholder="https://www.partner-website.com"
                        data-testid="create-partner-url"
                      />
                    </div>
                    <div className="form-field">
                      <label>Button-Text (Deutsch)</label>
                      <input
                        type="text"
                        value={formData.partner_button_de}
                        onChange={(e) => setFormData({ ...formData, partner_button_de: e.target.value })}
                        placeholder="z.B. Mehr erfahren"
                        data-testid="create-partner-btn-de"
                      />
                    </div>
                    <div className="form-field">
                      <label>Button Text (English)</label>
                      <input
                        type="text"
                        value={formData.partner_button_en}
                        onChange={(e) => setFormData({ ...formData, partner_button_en: e.target.value })}
                        placeholder="e.g. Learn more"
                        data-testid="create-partner-btn-en"
                      />
                    </div>
                    <div className="form-field">
                      <label>Texto del boton (Espanol)</label>
                      <input
                        type="text"
                        value={formData.partner_button_es}
                        onChange={(e) => setFormData({ ...formData, partner_button_es: e.target.value })}
                        placeholder="ej. Mas informacion"
                        data-testid="create-partner-btn-es"
                      />
                    </div>
                  </>
                )}
                <div className="form-field">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    Video aktiv (auf Landing Page anzeigen)
                  </label>
                </div>
              </div>
              <div className="form-actions">
                <button className="btn-cancel" onClick={() => { setShowCreateForm(false); resetForm(); }}>
                  <X size={18} />
                  Abbrechen
                </button>
                <button 
                  className="btn-save" 
                  onClick={handleCreate}
                  disabled={uploading}
                >
                  {uploading ? (
                    <>Laedt hoch...</>
                  ) : (
                    <>
                      <Save size={18} />
                      {uploadMode === 'upload' ? 'Video hochladen' : 'Video erstellen'}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {videos.length === 0 ? (
            <div className="empty-state">
              <PlayCircle size={48} />
              <p>Keine Videos vorhanden</p>
              <button className="btn-create" onClick={() => setShowCreateForm(true)}>
                <Plus size={18} />
                Erstes Video erstellen
              </button>
            </div>
          ) : (
            <div className="videos-grid">
              {videos.map((video) => (
                <div key={video.id} className="video-card-admin">
                  {editingVideo?.id === video.id ? (
                    <div className="video-form edit-form">
                      <div className="form-grid">
                        <div className="form-field">
                          <label>Titel</label>
                          <input
                            type="text"
                            value={editingVideo.title}
                            onChange={(e) => setEditingVideo({ ...editingVideo, title: e.target.value })}
                          />
                        </div>
                        <div className="form-field">
                          <label>Title</label>
                          <input
                            type="text"
                            value={editingVideo.title_en || ''}
                            onChange={(e) => setEditingVideo({ ...editingVideo, title_en: e.target.value })}
                          />
                        </div>
                        <div className="form-field">
                          <label>Titulo</label>
                          <input
                            type="text"
                            value={editingVideo.title_es || ''}
                            onChange={(e) => setEditingVideo({ ...editingVideo, title_es: e.target.value })}
                          />
                        </div>
                        <div className="form-field">
                          <label>Position</label>
                          <select
                            value={editingVideo.position}
                            onChange={(e) => setEditingVideo({ ...editingVideo, position: parseInt(e.target.value) })}
                          >
                            <option value={1}>1 (Startseite Links)</option>
                            <option value={2}>2 (Startseite Mitte)</option>
                            <option value={3}>3 (Startseite Rechts)</option>
                            <option value={4}>4 (Quiz nach Frage 8)</option>
                          </select>
                        </div>
                        <div className="form-field full-width">
                          <label>URL</label>
                          <input
                            type="text"
                            value={editingVideo.url}
                            onChange={(e) => setEditingVideo({ ...editingVideo, url: e.target.value })}
                          />
                        </div>
                        <div className="form-field full-width">
                          <label>Beschreibung</label>
                          <textarea
                            value={editingVideo.description || ''}
                            onChange={(e) => setEditingVideo({ ...editingVideo, description: e.target.value })}
                            rows={2}
                          />
                        </div>
                        <div className="form-field full-width">
                          <label>Description</label>
                          <textarea
                            value={editingVideo.description_en || ''}
                            onChange={(e) => setEditingVideo({ ...editingVideo, description_en: e.target.value })}
                            rows={2}
                          />
                        </div>
                        <div className="form-field full-width">
                          <label>Descripcion</label>
                          <textarea
                            value={editingVideo.description_es || ''}
                            onChange={(e) => setEditingVideo({ ...editingVideo, description_es: e.target.value })}
                            rows={2}
                          />
                        </div>
                        {editingVideo.position === 4 && (
                          <>
                            <div className="form-field full-width" style={{borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px'}}>
                              <label style={{fontWeight: 600, color: 'var(--color-violett)'}}>Werbepartner-Link</label>
                              <input
                                type="url"
                                value={editingVideo.partner_url || ''}
                                onChange={(e) => setEditingVideo({ ...editingVideo, partner_url: e.target.value })}
                                placeholder="https://www.partner-website.com"
                                data-testid="edit-partner-url"
                              />
                            </div>
                            <div className="form-field">
                              <label>Button-Text (Deutsch)</label>
                              <input
                                type="text"
                                value={editingVideo.partner_button_de || ''}
                                onChange={(e) => setEditingVideo({ ...editingVideo, partner_button_de: e.target.value })}
                                placeholder="z.B. Mehr erfahren"
                                data-testid="edit-partner-btn-de"
                              />
                            </div>
                            <div className="form-field">
                              <label>Button Text (English)</label>
                              <input
                                type="text"
                                value={editingVideo.partner_button_en || ''}
                                onChange={(e) => setEditingVideo({ ...editingVideo, partner_button_en: e.target.value })}
                                placeholder="e.g. Learn more"
                                data-testid="edit-partner-btn-en"
                              />
                            </div>
                            <div className="form-field">
                              <label>Texto del boton (Espanol)</label>
                              <input
                                type="text"
                                value={editingVideo.partner_button_es || ''}
                                onChange={(e) => setEditingVideo({ ...editingVideo, partner_button_es: e.target.value })}
                                placeholder="ej. Mas informacion"
                                data-testid="edit-partner-btn-es"
                              />
                            </div>
                          </>
                        )}
                        <div className="form-field">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={editingVideo.is_active}
                              onChange={(e) => setEditingVideo({ ...editingVideo, is_active: e.target.checked })}
                            />
                            Aktiv
                          </label>
                        </div>
                      </div>
                      <div className="form-actions">
                        <button className="btn-cancel" onClick={cancelEditing}>
                          <X size={18} />
                          Abbrechen
                        </button>
                        <button className="btn-save" onClick={() => handleUpdate(video.id)}>
                          <Save size={18} />
                          Speichern
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="video-preview">
                        <div className="video-position-badge">Position {video.position}</div>
                        <div className="video-type-badge">
                          {isCloudinaryUrl(video.url) ? 'Cloudinary' : isYoutubeUrl(video.url) ? 'YouTube' : 'Extern'}
                        </div>
                        {video.is_active ? (
                          <Eye size={16} className="video-status-icon active" />
                        ) : (
                          <EyeOff size={16} className="video-status-icon inactive" />
                        )}
                        {renderVideoPreview(video)}
                      </div>
                      <div className="video-info-admin">
                        <h4>{video.title}</h4>
                        {video.description && <p>{video.description}</p>}
                        <div className="video-stats">
                          <span className="stat-item">
                            <PlayCircle size={14} />
                            {video.click_count || 0} Klicks
                          </span>
                        </div>
                      </div>
                      <div className="video-actions">
                        <button className="btn-icon" onClick={() => startEditing(video)}>
                          <Edit2 size={18} />
                        </button>
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(video.id)}>
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoManager;
